import type { StreamMessage, StreamTopic } from "../contract/types";

/**
 * Client de flux temps réel (contrat §1, D-009).
 *
 * Un canal unique SSE `GET /networks/{n}/stream?topics=...`. Chaque événement
 * porte `{ topic, seq, at, payload }`. **`seq` est GLOBAL** : une seule séquence
 * monotone, tous sujets confondus. Chaque instantané REST d'un sujet porte le
 * `seq` auquel il est cohérent ; le consommateur applique les deltas de `seq`
 * strictement supérieur. Un `seq` non consécutif = un trou : recharger.
 *
 * Ce client :
 *  - ouvre / ferme le flux, gère la reconnexion (repli exponentiel) ;
 *  - parse et valide chaque message ;
 *  - suit le `seq` global et signale tout trou (`gap`) — sans savoir quel sujet
 *    a été manqué, donc `createSnapshotSync` recharge par prudence ;
 *  - signale une reconnexion (`reconnect`) : des messages ont pu être perdus.
 */

export type StreamStatus = "idle" | "connecting" | "open" | "reconnecting" | "closed";

export interface StreamEvents {
  status: (status: StreamStatus) => void;
  /** Message en séquence (ou tout premier message). */
  message: (message: StreamMessage) => void;
  /**
   * Trou dans la séquence globale : `expected` (seq attendu) vs `received`.
   * L'appelant recharge ses instantanés REST et appelle `observeSeq(nouveauSeq)`.
   */
  gap: (info: { expected: number; received: number }) => void;
  /** Le flux s'est rétabli après une coupure : recharger tous les instantanés. */
  reconnect: () => void;
  /** Erreur de transport non fatale (le client va retenter). */
  error: (error: unknown) => void;
}

export interface StreamHandle {
  close(): void;
}

/** Transport injectable : par défaut `EventSource`, remplacé par la couche de mocks. */
export interface StreamTransport {
  open(
    url: string,
    handlers: {
      onMessage: (raw: string) => void;
      onOpen: () => void;
      onError: (error: unknown) => void;
    },
  ): StreamHandle;
}

export interface StreamClientOptions {
  url: string;
  topics: StreamTopic[];
  transport?: StreamTransport;
  /** Repli : délais successifs de reconnexion en ms. Le dernier est répété. */
  backoffMs?: number[];
  clock?: { setTimeout: typeof setTimeout; clearTimeout: typeof clearTimeout };
}

const DEFAULT_BACKOFF = [1_000, 2_000, 5_000, 10_000, 30_000];
const KNOWN_TOPICS: ReadonlySet<string> = new Set<StreamTopic>([
  "journeys",
  "deviations",
  "alerts",
  "sources",
  "junctions",
]);

function isStreamMessage(value: unknown): value is StreamMessage {
  if (typeof value !== "object" || value === null) return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.topic === "string" &&
    KNOWN_TOPICS.has(m.topic) &&
    typeof m.seq === "number" &&
    Number.isFinite(m.seq) &&
    typeof m.at === "string" &&
    "payload" in m
  );
}

class Emitter {
  private readonly listeners = new Map<keyof StreamEvents, Set<(...args: never[]) => void>>();

  on<K extends keyof StreamEvents>(event: K, listener: StreamEvents[K]): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener as (...args: never[]) => void);
    return () => set.delete(listener as (...args: never[]) => void);
  }

  emit<K extends keyof StreamEvents>(event: K, ...args: Parameters<StreamEvents[K]>): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const listener of [...set]) (listener as (...a: unknown[]) => void)(...args);
  }

  clear(): void {
    this.listeners.clear();
  }
}

export class StreamClient {
  private readonly options: Required<Omit<StreamClientOptions, "transport">> & { transport: StreamTransport };
  private readonly emitter = new Emitter();

  /** Dernier `seq` global vu (tous sujets). `null` avant le premier message. */
  private lastSeq: number | null = null;

  private handle: StreamHandle | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryIndex = 0;
  private hadOpen = false;
  private stopped = false;
  private _status: StreamStatus = "idle";

  constructor(options: StreamClientOptions) {
    this.options = {
      url: options.url,
      topics: options.topics,
      transport: options.transport ?? defaultEventSourceTransport(),
      backoffMs: options.backoffMs ?? DEFAULT_BACKOFF,
      clock: options.clock ?? { setTimeout, clearTimeout },
    };
  }

  get status(): StreamStatus {
    return this._status;
  }

  on<K extends keyof StreamEvents>(event: K, listener: StreamEvents[K]): () => void {
    return this.emitter.on(event, listener);
  }

  start(): void {
    if (this.stopped) throw new Error("StreamClient déjà arrêté — en créer un nouveau.");
    if (this.handle || this.retryTimer) return;
    this.connect(false);
  }

  stop(): void {
    this.stopped = true;
    this.clearRetry();
    this.handle?.close();
    this.handle = null;
    this.setStatus("closed");
    this.emitter.clear();
  }

  /**
   * Ancre le `seq` global après (re)chargement d'un instantané REST. Monotone :
   * n'abaisse jamais `lastSeq` (plusieurs synchros peuvent l'appeler avec des
   * instantanés pris à des instants différents).
   */
  observeSeq(seq: number): void {
    this.lastSeq = this.lastSeq === null ? seq : Math.max(this.lastSeq, seq);
  }

  /** Dernier `seq` global connu, ou `undefined`. */
  currentSeq(): number | undefined {
    return this.lastSeq ?? undefined;
  }

  private connect(isRetry: boolean): void {
    this.setStatus(isRetry ? "reconnecting" : "connecting");
    this.handle = this.options.transport.open(this.options.url, {
      onOpen: () => {
        const wasDrop = this.hadOpen;
        this.hadOpen = true;
        this.retryIndex = 0;
        this.setStatus("open");
        if (wasDrop) this.emitter.emit("reconnect");
      },
      onMessage: (raw) => this.handleRaw(raw),
      onError: (error) => {
        this.emitter.emit("error", error);
        this.scheduleRetry();
      },
    });
  }

  private handleRaw(raw: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      this.emitter.emit("error", new Error(`Message de flux illisible: ${(error as Error).message}`));
      return;
    }
    if (!isStreamMessage(parsed)) {
      this.emitter.emit("error", new Error("Message de flux non conforme au contrat §1."));
      return;
    }

    const message = parsed;

    if (this.lastSeq === null) {
      this.lastSeq = message.seq;
      this.emitter.emit("message", message);
      return;
    }
    if (message.seq <= this.lastSeq) {
      return; // doublon ou message d'avant l'instantané
    }
    if (message.seq !== this.lastSeq + 1) {
      this.emitter.emit("gap", { expected: this.lastSeq + 1, received: message.seq });
      // On avance la séquence mais on n'émet PAS le message : les consommateurs
      // rechargent leurs instantanés, qui repartiront d'un seq ≥ received.
      this.lastSeq = message.seq;
      return;
    }

    this.lastSeq = message.seq;
    this.emitter.emit("message", message);
  }

  private scheduleRetry(): void {
    this.handle?.close();
    this.handle = null;
    if (this.stopped || this.retryTimer) return;
    const delays = this.options.backoffMs;
    const delay = delays[Math.min(this.retryIndex, delays.length - 1)] ?? 30_000;
    this.retryIndex += 1;
    this.setStatus("reconnecting");
    this.retryTimer = this.options.clock.setTimeout(() => {
      this.retryTimer = null;
      if (!this.stopped) this.connect(true);
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer) {
      this.options.clock.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private setStatus(status: StreamStatus): void {
    if (this._status === status) return;
    this._status = status;
    this.emitter.emit("status", status);
  }
}

/**
 * Transport SSE par défaut, adossé à `EventSource`.
 *
 * `withCredentials: true` **est désormais requis** : la porte d'accès
 * applicative (contrat v1.38 §8bis) garde le canal `/stream` comme le reste de
 * l'API — sans le cookie `sentinelle_session`, la connexion SSE répond `401`. Le
 * cookie ne part sur une requête `EventSource` que si `withCredentials` est
 * armé.
 *
 * Le piège cross-origin du 2026-09-05 (connexion bloquée par CORS, rechargée en
 * boucle) est levé côté backend : `build()` enregistre `@fastify/cors` avec
 * `credentials: true`, et le canal SSE — réponse `hijack()`ée, hors du hook
 * `onSend` de `@fastify/cors` — repose manuellement l'en-tête d'origine via
 * `resolveCorsOrigin` (voir `apps/api/src/server.ts`). En production front et
 * API partagent l'origine : pas de CORS du tout.
 */
export function defaultEventSourceTransport(): StreamTransport {
  return {
    open(url, handlers) {
      const source = new EventSource(url, { withCredentials: true });
      source.onopen = () => handlers.onOpen();
      source.onmessage = (event: MessageEvent<string>) => handlers.onMessage(event.data);
      source.onerror = (event) => handlers.onError(event);
      return {
        close() {
          source.close();
        },
      };
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Coordination instantané REST ↔ deltas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Encapsule « charge un instantané, puis applique les deltas » pour un sujet,
 * robuste aux trous et aux reconnexions.
 *
 *  - `loadSnapshot()` renvoie l'état complet ET le `seq` global auquel il est
 *    cohérent ;
 *  - seuls les deltas de `seq` **strictement supérieur** à celui de l'instantané
 *    sont appliqués (D-009) ;
 *  - un `gap` global ou une reconnexion relancent `loadSnapshot`.
 *
 * Les deltas reçus avant que l'instantané soit prêt sont mis en tampon puis
 * rejoués (filtrés par le même seuil de `seq`).
 */
export interface SnapshotSyncConfig<TState, TPayload> {
  topic: StreamTopic;
  stream: StreamClient;
  loadSnapshot: (signal: AbortSignal) => Promise<{ state: TState; seq?: number }>;
  applyDelta: (state: TState, payload: TPayload) => TState;
  onState: (state: TState) => void;
  onError?: (error: unknown) => void;
}

export interface SnapshotSync {
  refresh(): void;
  dispose(): void;
}

export function createSnapshotSync<TState, TPayload>(config: SnapshotSyncConfig<TState, TPayload>): SnapshotSync {
  const { topic, stream, loadSnapshot, applyDelta, onState, onError } = config;

  let state: TState | undefined;
  let snapshotSeq = -Infinity;
  let ready = false;
  let generation = 0;
  let pending: Array<StreamMessage<TPayload>> = [];
  let abort: AbortController | null = null;
  let disposed = false;

  const load = () => {
    if (disposed) return;
    generation += 1;
    const gen = generation;
    ready = false;
    abort?.abort();
    abort = new AbortController();

    loadSnapshot(abort.signal)
      .then(({ state: snapshot, seq }) => {
        if (disposed || gen !== generation) return;
        state = snapshot;
        snapshotSeq = seq ?? -Infinity;
        if (seq !== undefined) stream.observeSeq(seq);
        for (const message of pending) {
          if (message.seq > snapshotSeq) state = applyDelta(state, message.payload);
        }
        pending = [];
        ready = true;
        onState(state);
      })
      .catch((error) => {
        if (disposed || gen !== generation) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
        onError?.(error);
      });
  };

  const offMessage = stream.on("message", (message) => {
    if (message.topic !== topic) return;
    const typed = message as StreamMessage<TPayload>;
    if (!ready || state === undefined) {
      pending.push(typed);
      return;
    }
    if (typed.seq <= snapshotSeq) return;
    state = applyDelta(state, typed.payload);
    onState(state);
  });

  const offGap = stream.on("gap", () => load());
  const offReconnect = stream.on("reconnect", () => load());

  load();

  return {
    refresh: load,
    dispose() {
      disposed = true;
      abort?.abort();
      offMessage();
      offGap();
      offReconnect();
    },
  };
}
